import { useState, useEffect } from 'react';
import { Line, LineChart, Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { gmpService } from '@/lib/dataService';
import { formatInr } from '@/lib/numberFormat';

interface GMPChartData {
  date: string;
  gmp: number;
  trend?: 'up' | 'down' | 'stable';
}

type ChartType = 'line' | 'bar' | 'area';

const AdminGMPChart = ({ ipoId }: { ipoId: string }) => {
  const [chartData, setChartData] = useState<GMPChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState<ChartType>('line');

  const mapHistoryToChartData = (history: Array<{ date: string; gmp_value: number | string }>): GMPChartData[] => {
    const sorted = [...history].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return sorted.map((entry, index) => {
      const currentGmp = Number(entry.gmp_value);
      const safeCurrentGmp = Number.isFinite(currentGmp) ? currentGmp : 0;
      const prevGmpRaw = index > 0 ? Number(sorted[index - 1].gmp_value) : safeCurrentGmp;
      const prevGmp = Number.isFinite(prevGmpRaw) ? prevGmpRaw : safeCurrentGmp;
      const trend = safeCurrentGmp > prevGmp ? 'up' : safeCurrentGmp < prevGmp ? 'down' : 'stable';

      return {
        date: new Date(entry.date).toLocaleDateString('en-IN', {
          month: 'short',
          day: 'numeric',
        }),
        gmp: safeCurrentGmp,
        trend,
      };
    });
  };

  const formatYAxisTick = (value: number) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0';

    const normalized = Math.abs(numeric) < 0.000001 ? 0 : numeric;
    const rounded = Number(normalized.toFixed(1));
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  const roundAxisValue = (value: number) => Number(value.toFixed(1));

  const yAxisDomain: [number, number] = (() => {
    if (!chartData.length) {
      return [-1, 1];
    }

    const values = chartData.map((item) => item.gmp);
    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
      const padding = Math.max(1, Math.abs(min) * 0.25);
      return [roundAxisValue(min - padding), roundAxisValue(max + padding)];
    }

    const padding = Math.max(1, (max - min) * 0.15);
    return [roundAxisValue(min - padding), roundAxisValue(max + padding)];
  })();

  useEffect(() => {
    const loadGMPHistory = async () => {
      try {
        setLoading(true);
        
        const history = await gmpService.getHistory(ipoId);
        
        if (!history.length) {
          setChartData([]);
          return;
        }

        setChartData(mapHistoryToChartData(history));
      } catch (error) {
        console.error('Error loading GMP history:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    if (ipoId) {
      loadGMPHistory();
    }
  }, [ipoId]);

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={18} className="text-muted-foreground" />
            <YAxis
              tick={{ fontSize: 10 }}
              width={44}
              domain={yAxisDomain}
              tickFormatter={formatYAxisTick}
              className="text-muted-foreground"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number) => [formatInr(Number(value)), 'GMP']}
            />
            <Bar dataKey="gmp" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={18} className="text-muted-foreground" />
            <YAxis
              tick={{ fontSize: 10 }}
              width={44}
              domain={yAxisDomain}
              tickFormatter={formatYAxisTick}
              className="text-muted-foreground"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number) => [formatInr(Number(value)), 'GMP']}
            />
            <Area 
              type="monotone" 
              dataKey="gmp" 
              stroke="hsl(var(--primary))" 
              fill="hsl(var(--primary))"
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </AreaChart>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={18} className="text-muted-foreground" />
            <YAxis
              tick={{ fontSize: 10 }}
              width={44}
              domain={yAxisDomain}
              tickFormatter={formatYAxisTick}
              className="text-muted-foreground"
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px'
              }}
              formatter={(value: number) => [formatInr(Number(value)), 'GMP']}
            />
            <Line 
              type="monotone" 
              dataKey="gmp" 
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        );
    }
  };

  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading GMP chart...</div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="text-muted-foreground text-sm">No GMP data available</div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Chart Type Selector */}
      <div className="flex gap-1 mb-2">
        <button
          onClick={() => setChartType('line')}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            chartType === 'line' 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Line
        </button>
        <button
          onClick={() => setChartType('bar')}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            chartType === 'bar' 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Bar
        </button>
        <button
          onClick={() => setChartType('area')}
          className={`px-2 py-1 text-xs rounded transition-colors ${
            chartType === 'area' 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Area
        </button>
      </div>

      {/* Chart */}
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* Current GMP Display */}
      <div className="mt-2 p-2 bg-muted/20 rounded text-xs">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Current GMP:</span>
          <span className="font-semibold text-foreground">{formatInr(chartData[chartData.length - 1]?.gmp || 0)}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminGMPChart;
